/**
 * Radius Circles Utilities
 *
 * This module handles all radius circle operations for the Interactive Survey Market Map.
 * It provides functions to add, remove, and manage radius circles around properties using
 * Mapbox GL JS and Turf.js for geospatial calculations.
 */

import { Property } from "@/features/survey/types";
import { circle, destination, point } from "@turf/turf";
import mapboxgl from "mapbox-gl";
import { MutableRefObject } from "react";

// Types for radius circle configuration
export interface RadiusData {
  radius?: Array<{ value: string | number }> | number;
  radiusLength?: string;
  radiusColor?: string;
  radiusLabel?: boolean;
}

// Types for radius circle references
export interface RadiusCircleRefs {
  radiusCirclesRef: MutableRefObject<Set<string>>;
  radiusLabelMarkersRef: MutableRefObject<Map<string, mapboxgl.Marker>>;
}

/**
 * Adds a radius circle around a property on the map
 */
export const addRadiusCircle = (
  map: mapboxgl.Map,
  property: Property,
  radiusData: RadiusData,
  radiusIndex: number,
  getCoordinates: (property: Property) => [number, number],
  refs: RadiusCircleRefs,
): void => {
  if (!map) {
    throw new Error("No map available for radius circle");
  }
  // Handle different parameter formats for backward compatibility
  let radiusValue: number;
  let radiusLength: "miles" | "kilometers" | "meters" = "miles";
  let fillColor: string = "#007cbf";

  if (typeof radiusData === "object" && radiusData !== null) {
    if ("radius" in radiusData && Array.isArray(radiusData.radius)) {
      // Extract radius from array using radiusIndex
      const radiusArray = radiusData.radius as Array<{
        value: string | number;
      }>;
      if (radiusIndex < radiusArray.length && radiusArray[radiusIndex]) {
        const radiusItem = radiusArray[radiusIndex];
        radiusValue =
          typeof radiusItem.value === "string"
            ? parseFloat(radiusItem.value)
            : radiusItem.value;
      } else {
        radiusValue = 3; // Default fallback
      }
    } else if (
      "radius" in radiusData &&
      typeof radiusData.radius === "number"
    ) {
      // Direct radius value
      radiusValue = radiusData.radius;
    } else {
      radiusValue = 3; // Default fallback
    }

    // Convert units
    if ("radiusLength" in radiusData) {
      switch (radiusData.radiusLength) {
        case "MI":
          radiusLength = "miles";
          break;
        case "KM":
          radiusLength = "kilometers";
          break;
        default:
          radiusLength = "meters";
          break;
      }
    }

    if ("radiusColor" in radiusData) {
      fillColor = radiusData.radiusColor || "#007cbf";
    }
  } else {
    console.warn(">>> Invalid radius data provided to addRadiusCircle");
    return;
  }

  const coordinates = getCoordinates(property);

  // Validate coordinates are valid numbers
  if (!Array.isArray(coordinates) || coordinates.length !== 2) {
    console.error(
      `>>> Invalid coordinates array for property ${property.id}:`,
      coordinates,
    );
    return;
  }

  const [lng, lat] = coordinates;
  if (
    typeof lng !== "number" ||
    typeof lat !== "number" ||
    isNaN(lng) ||
    isNaN(lat)
  ) {
    console.error(
      `>>> Invalid coordinate values for property ${property.id}:`,
      { lng, lat },
    );
    return;
  }

  // Validate radius value
  if (
    typeof radiusValue !== "number" ||
    isNaN(radiusValue) ||
    radiusValue <= 0
  ) {
    console.error(
      `>>> Invalid radius value for property ${property.id}:`,
      radiusValue,
    );
    return;
  }

  // Validate radius length unit
  const validUnits = ["miles", "kilometers", "meters"];
  if (!validUnits.includes(radiusLength)) {
    console.error(
      `>>> Invalid radius length unit for property ${property.id}:`,
      radiusLength,
    );
    return;
  }

  const sourceId = `radius_${property.id}_${radiusIndex}`;
  const layerId = `radius_circle_${property.id}_${radiusIndex}`;

  let center;
  let circleGeometry;

  try {
    // Create a circle geometry with the actual radius value
    center = point([lng, lat]);
    circleGeometry = circle(center, radiusValue, {
      units: radiusLength,
    });
  } catch (error) {
    console.error(
      `>>> Error creating circle geometry for property ${property.id}:`,
      {
        coordinates: [lng, lat],
        radiusValue,
        radiusLength,
        error: error instanceof Error ? error.message : String(error),
      },
    );
    return;
  }

  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, {
      type: "geojson",
      data: circleGeometry,
    });

    map.addLayer({
      id: layerId,
      type: "line",
      source: sourceId,
      paint: {
        "line-color": fillColor,
        "line-width": 3,
      },
    });

    // Add label if radius data contains label information
    if (
      typeof radiusData === "object" &&
      radiusData !== null &&
      radiusData.radiusLabel
    ) {
      createRadiusLabel(
        map,
        property,
        radiusData,
        radiusIndex,
        radiusValue,
        radiusLength,
        fillColor,
        center,
        refs.radiusLabelMarkersRef,
      );
    }
  }
  refs.radiusCirclesRef.current.add(`${property.id}_${radiusIndex}`);
};

/**
 * Creates a label marker for a radius circle
 */
const createRadiusLabel = (
  map: mapboxgl.Map,
  property: Property,
  radiusData: RadiusData,
  radiusIndex: number,
  radiusValue: number,
  radiusLength: "miles" | "kilometers" | "meters",
  fillColor: string,
  center: ReturnType<typeof point>,
  radiusLabelMarkersRef: MutableRefObject<Map<string, mapboxgl.Marker>>,
): void => {
  const isRenderedMap = window.location.pathname.includes("/marketmapRendered");
  // Calculate the label position on the circle's edge
  const bearingDegrees = 0;

  // Calculate the destination point on the circle's edge
  const labelPosition = destination(center, radiusValue, bearingDegrees, {
    units: radiusLength,
  });

  // Get the original radius value for display (before any unit conversion)
  let displayValue = radiusValue;
  let displayUnit = radiusData.radiusLength || "MI";

  // If we converted the radius for calculation, convert back for display
  if (
    typeof radiusData === "object" &&
    "radius" in radiusData &&
    Array.isArray(radiusData.radius)
  ) {
    const radiusArray = radiusData.radius as Array<{
      value: string | number;
    }>;
    if (radiusIndex < radiusArray.length && radiusArray[radiusIndex]) {
      const originalValue = radiusArray[radiusIndex].value;
      displayValue =
        typeof originalValue === "string"
          ? parseFloat(originalValue)
          : originalValue;
    }
  } else if (
    typeof radiusData === "object" &&
    "radius" in radiusData &&
    typeof radiusData.radius === "number"
  ) {
    // For direct number values, use the original radius value
    displayValue = radiusData.radius;
  }

  const labelDiv = document.createElement("div");
  const labelDivRendered = document.createElement("div");
  labelDiv.style.backgroundColor = fillColor;
  labelDiv.style.color = "#ffffff";
  labelDiv.style.padding = "0px 4px";
  labelDiv.style.borderRadius = "3px";
  labelDiv.style.fontSize = "11px";
  labelDiv.style.whiteSpace = "nowrap";
  labelDiv.style.border = "1px solid rgba(255,255,255,0.3)";
  labelDiv.style.boxShadow = "0 1px 2px rgba(0,0,0,0.3)";
  labelDiv.innerText = `${displayValue} ${displayUnit}`;
  labelDiv.className = "radius-label";
  if (isRenderedMap) {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");

    // Calculate dynamic width based on text content
    const textContent = `${displayValue} ${displayUnit}`;
    const fontSize = 10;
    const paddingHorizontal = 8; // 4px padding on each side
    const minWidth = 40;
    // Rough estimate: 0.4 * fontSize per character for most fonts
    const estimatedTextWidth = textContent.length * fontSize * 0.4;
    const dynamicWidth = Math.max(
      minWidth,
      estimatedTextWidth + paddingHorizontal,
    );

    svg.setAttribute("width", Math.ceil(dynamicWidth).toString());
    svg.setAttribute("height", "13");
    svg.setAttribute("viewBox", `0 0 ${Math.ceil(dynamicWidth)} 13`);
    svg.setAttribute("class", "radius-svg");

    // Create rectangular background with dynamic width
    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("x", "2");
    rect.setAttribute("y", "2");
    rect.setAttribute("width", (dynamicWidth - 4).toString());
    rect.setAttribute("height", "10");
    rect.setAttribute("fill", fillColor);
    rect.setAttribute("rx", "1");
    rect.setAttribute("ry", "1");

    const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
    text.setAttribute("x", "50%");
    text.setAttribute("y", "50%");
    text.setAttribute("text-anchor", "middle");
    text.setAttribute("dy", ".4em");
    text.setAttribute("fill", "#fff");
    text.setAttribute("font-size", "8px");
    text.setAttribute("class", "radius-label");
    text.textContent = textContent;

    svg.appendChild(rect);
    svg.appendChild(text);
    labelDivRendered.appendChild(svg);
  }

  // Create and add the marker at the calculated position
  const labelLng = labelPosition.geometry.coordinates[0];
  const labelLat = labelPosition.geometry.coordinates[1];
  if (
    labelLng == null ||
    labelLat == null ||
    isNaN(labelLng) ||
    isNaN(labelLat)
  ) {
    return;
  }
  const labelMarker = new mapboxgl.Marker(
    isRenderedMap ? labelDivRendered : labelDiv,
  )
    .setLngLat([labelLng, labelLat])
    .addTo(map);

  // Store the label marker for cleanup
  const labelMarkerId = `label_${property.id}_${radiusIndex}`;
  radiusLabelMarkersRef.current.set(labelMarkerId, labelMarker);
};

/**
 * Removes all radius circles for a specific property
 */
export const removeRadiusCircle = (
  map: mapboxgl.Map | null,
  propertyId: string,
  refs: RadiusCircleRefs,
): void => {
  if (!map) return;

  // Find and remove all radius circles for this property
  const circleIds = Array.from(refs.radiusCirclesRef.current).filter((id) =>
    id.startsWith(`${propertyId}_`),
  );

  circleIds.forEach((circleId) => {
    const parts = circleId.split("_");
    const index = parts[parts.length - 1]; // Get the last part as radius index
    const propId = parts.slice(0, -1).join("_"); // Everything except the last part is the property ID
    const sourceId = `radius_${propId}_${index}`;
    const layerId = `radius_circle_${propId}_${index}`;
    const labelMarkerId = `label_${propId}_${index}`;

    // Remove layer and source if they exist
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }

    // Remove label marker if it exists
    const labelMarker = refs.radiusLabelMarkersRef.current.get(labelMarkerId);
    if (labelMarker) {
      labelMarker.remove();
      refs.radiusLabelMarkersRef.current.delete(labelMarkerId);
    }

    refs.radiusCirclesRef.current.delete(circleId);
  });
};

/**
 * Removes all radius circles from the map
 */
export const clearAllRadiusCircles = (
  map: mapboxgl.Map | null,
  refs: RadiusCircleRefs,
): void => {
  // Get all unique property IDs from the circle IDs
  const propertyIds = new Set<string>();
  refs.radiusCirclesRef.current.forEach((circleId) => {
    // Extract property ID from circleId format: "propertyId_radiusIndex"
    const parts = circleId.split("_");
    const propertyId = parts.slice(0, -1).join("_"); // Everything except the last part is the property ID
    propertyIds.add(propertyId);
  });

  // Remove circles for each unique property
  propertyIds.forEach((propertyId) => {
    removeRadiusCircle(map, propertyId, refs);
  });

  // Clear all references
  refs.radiusCirclesRef.current.clear();

  // Also clear any remaining label markers
  refs.radiusLabelMarkersRef.current.forEach((marker) => {
    marker.remove();
  });
  refs.radiusLabelMarkersRef.current.clear();
};
